/**
 * PropertyRollupService
 *
 * Manages rollup property computations - aggregating values from related objects.
 *
 * Features:
 * - Rollup configuration management (CRUD)
 * - Server-side computation via Edge Function
 * - Local computation fallback for offline mode
 * - Cache management with real-time invalidation
 * - Full Supabase sync integration
 *
 * Examples:
 * - SUM(hours) from all completed tasks in a project
 * - COUNT of contacts in a company
 * - AVERAGE rating of books in a series
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { restSelect, restInsert, restUpdate, restDelete } from '../lib/supabaseRest';
import { propertyRelationService } from './PropertyRelationService';
import type {
  PropertyRollup,
  PropertyComputedCache,
  CreateRollupRequest,
  ComputeRollupResponse,
  AggregationFunction,
} from '../types/advanced-properties';
import type { Property } from '../models/types';

// Edge Function URL (from environment or fallback)
const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'http://localhost:54321/functions/v1';

export class PropertyRollupService {
  /**
   * Get rollup configuration for a property
   */
  async getRollup(propertyId: string): Promise<PropertyRollup | null> {
    try {
      const rollup = await db.propertyRollups.get({ propertyId });
      return rollup || null;
    } catch (error) {
      console.error('[PropertyRollupService] Error getting rollup:', error);
      return null;
    }
  }

  /**
   * Create a new rollup configuration
   */
  async createRollup(request: CreateRollupRequest): Promise<PropertyRollup> {
    try {
      // Validate that the relation property exists and is a relation type
      const relationProperty = await db.properties.get(request.relation_property_id);
      if (!relationProperty) {
        throw new Error('Relation property not found');
      }
      if (relationProperty.type !== 'relation' && relationProperty.type !== 'multi-relation') {
        throw new Error('Relation property must be of type relation or multi-relation');
      }

      const rollup: PropertyRollup = {
        id: this.generateId(),
        property_id: request.property_id,
        relation_property_id: request.relation_property_id,
        target_property_name: request.target_property_name,
        aggregation_function: request.aggregation_function,
        filter_conditions: request.filter_conditions || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Store in Supabase
      const created = await restInsert<PropertyRollup>('property_rollups', [rollup]);

      // Store locally
      await db.propertyRollups.add(rollup);

      // Emit event
      eventBus.emit('rollup:created', {
        propertyId: rollup.property_id,
        rollup: created[0],
      });

      return created[0];
    } catch (error) {
      console.error('[PropertyRollupService] Error creating rollup:', error);
      throw error;
    }
  }

  /**
   * Update rollup configuration
   */
  async updateRollup(
    rollupId: string,
    updates: Partial<Omit<PropertyRollup, 'id' | 'property_id' | 'created_at'>>
  ): Promise<PropertyRollup> {
    try {
      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // Update in Supabase
      await restUpdate(
        'property_rollups',
        updatedData,
        { eq: { id: rollupId } }
      );

      // Update locally
      await db.propertyRollups.update(rollupId, updatedData);

      // Invalidate cache for this rollup
      const rollup = await db.propertyRollups.get(rollupId);
      if (rollup) {
        await this.invalidateCache(rollup.property_id);
      }

      // Emit event
      eventBus.emit('rollup:updated', { rollupId, updates });

      // Fetch updated rollup
      const updated = await db.propertyRollups.get(rollupId);
      if (!updated) {
        throw new Error('Rollup not found after update');
      }

      return updated;
    } catch (error) {
      console.error('[PropertyRollupService] Error updating rollup:', error);
      throw error;
    }
  }

  /**
   * Delete a rollup configuration
   */
  async deleteRollup(rollupId: string): Promise<void> {
    try {
      const rollup = await db.propertyRollups.get(rollupId);
      if (!rollup) return;

      // Delete from Supabase
      await restDelete('property_rollups', { eq: { id: rollupId } });

      // Delete locally
      await db.propertyRollups.delete(rollupId);

      // Clear cache
      await this.clearCache(rollup.property_id);

      // Emit event
      eventBus.emit('rollup:deleted', {
        propertyId: rollup.property_id,
        rollupId,
      });
    } catch (error) {
      console.error('[PropertyRollupService] Error deleting rollup:', error);
      throw error;
    }
  }

  /**
   * Compute rollup value for a specific object
   * Tries server-side Edge Function first, falls back to local computation
   */
  async computeRollup(
    propertyId: string,
    objectId: string
  ): Promise<ComputeRollupResponse> {
    try {
      // Check cache first
      const cached = await this.getCache(propertyId, objectId);
      if (cached && !cached.is_stale) {
        return {
          value: cached.computed_value,
          source_count: cached.computed_value?.source_count || 0,
        };
      }

      // Get rollup configuration
      const rollup = await this.getRollup(propertyId);
      if (!rollup) {
        throw new Error('Rollup configuration not found');
      }

      // Try server-side computation first
      try {
        const result = await this.computeRollupServerSide(rollup, objectId);
        await this.setCache(propertyId, objectId, result);
        return result;
      } catch (serverError) {
        console.warn('[PropertyRollupService] Server-side computation failed, trying local:', serverError);

        // Fall back to local computation
        const result = await this.computeRollupLocally(rollup, objectId);
        await this.setCache(propertyId, objectId, result);
        return result;
      }
    } catch (error) {
      console.error('[PropertyRollupService] Error computing rollup:', error);
      throw error;
    }
  }

  /**
   * Compute preview of rollup without requiring saved configuration
   * Used by RollupConfigurator to show live preview
   */
  async computePreview(
    relationPropertyId: string,
    targetPropertyName: string,
    aggregationFunction: AggregationFunction,
    objectId: string,
    filterConditions?: Record<string, any>
  ): Promise<ComputeRollupResponse> {
    try {
      // Create temporary rollup object for computation
      const tempRollup: PropertyRollup = {
        id: 'preview',
        property_id: 'preview',
        relation_property_id: relationPropertyId,
        target_property_name: targetPropertyName,
        aggregation_function: aggregationFunction,
        filter_conditions: filterConditions || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: '',
        synced: false,
      };

      // Compute locally (don't try server-side for preview)
      return await this.computeRollupLocally(tempRollup, objectId);
    } catch (error) {
      console.error('[PropertyRollupService] Error computing preview:', error);
      throw error;
    }
  }

  /**
   * Compute rollup using Edge Function
   */
  private async computeRollupServerSide(
    rollup: PropertyRollup,
    objectId: string
  ): Promise<ComputeRollupResponse> {
    const response = await fetch(`${EDGE_FUNCTION_URL}/compute-rollup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`,
      },
      body: JSON.stringify({
        propertyId: rollup.property_id,
        objectId,
        relationPropertyId: rollup.relation_property_id,
        targetPropertyName: rollup.target_property_name,
        aggregationFunction: rollup.aggregation_function,
        filterCondition: rollup.filter_conditions,
      }),
    });

    if (!response.ok) {
      throw new Error(`Edge Function error: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Computation failed');
    }

    return {
      value: data.data.value,
      source_count: data.data.count || 0,
    };
  }

  /**
   * Compute rollup locally (offline fallback)
   */
  private async computeRollupLocally(
    rollup: PropertyRollup,
    objectId: string
  ): Promise<ComputeRollupResponse> {
    // Get related objects
    const relatedObjects = await propertyRelationService.getRelatedObjects(
      rollup.relation_property_id,
      objectId
    );

    // Filter objects if filter conditions exist
    let filteredObjects = relatedObjects;
    if (rollup.filter_conditions) {
      filteredObjects = this.applyFilters(relatedObjects, rollup.filter_conditions);
    }

    // Get target property values from related objects
    const values: any[] = [];
    for (const obj of filteredObjects) {
      const props = await db.properties
        .where({ objectId: obj.id, name: rollup.target_property_name })
        .toArray();

      if (props.length > 0 && props[0].value) {
        values.push(props[0].value.value || props[0].value.computed);
      }
    }

    // Compute aggregation
    const aggregatedValue = this.aggregate(values, rollup.aggregation_function);

    return {
      value: aggregatedValue,
      source_count: filteredObjects.length,
    };
  }

  /**
   * Apply filter conditions to objects
   */
  private applyFilters(objects: any[], filters: Record<string, any>): any[] {
    return objects.filter(obj => {
      for (const [key, value] of Object.entries(filters)) {
        const objValue = obj[key] || obj.properties?.[key];
        if (objValue !== value) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Aggregate values using the specified function
   */
  private aggregate(values: any[], func: AggregationFunction): number | string | null {
    if (values.length === 0) {
      if (func === 'count' || func === 'count_unique') return 0;
      if (func === 'concat') return '';
      return null;
    }

    switch (func) {
      case 'count':
        return values.length;

      case 'count_unique':
        return new Set(values).size;

      case 'sum':
        return values.reduce((sum, val) => sum + (Number(val) || 0), 0);

      case 'average': {
        const numericValues = values.map(v => Number(v)).filter(v => !isNaN(v));
        if (numericValues.length === 0) return null;
        return numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
      }

      case 'max': {
        const numericValues = values.map(v => Number(v)).filter(v => !isNaN(v));
        if (numericValues.length === 0) return null;
        return Math.max(...numericValues);
      }

      case 'min': {
        const numericValues = values.map(v => Number(v)).filter(v => !isNaN(v));
        if (numericValues.length === 0) return null;
        return Math.min(...numericValues);
      }

      case 'concat':
        return values
          .filter(v => v != null && v !== '')
          .map(v => String(v))
          .join(', ');

      case 'percent_empty':
        const emptyCount = values.filter(v => v == null || v === '').length;
        return (emptyCount / values.length) * 100;

      case 'percent_filled':
        const filledCount = values.filter(v => v != null && v !== '').length;
        return (filledCount / values.length) * 100;

      default:
        return null;
    }
  }

  /**
   * Get cached computed value
   */
  private async getCache(
    propertyId: string,
    objectId: string
  ): Promise<PropertyComputedCache | null> {
    try {
      const cache = await db.propertyComputedCache
        .where({ propertyId, objectId })
        .first();
      return cache || null;
    } catch (error) {
      console.error('[PropertyRollupService] Error getting cache:', error);
      return null;
    }
  }

  /**
   * Set cached computed value
   */
  private async setCache(
    propertyId: string,
    objectId: string,
    result: ComputeRollupResponse
  ): Promise<void> {
    try {
      const cache: PropertyComputedCache = {
        id: this.generateId(),
        property_id: propertyId,
        object_id: objectId,
        computed_value: result.value,
        computed_at: new Date().toISOString(),
        is_stale: false,
      };

      // Upsert in Supabase
      await restInsert<PropertyComputedCache>('property_computed_cache', [cache], {
        onConflict: 'property_id,object_id',
      });

      // Upsert locally
      const existing = await this.getCache(propertyId, objectId);
      if (existing) {
        await db.propertyComputedCache.update(existing.id, {
          computed_value: result.value,
          computed_at: cache.computed_at,
          is_stale: false,
        });
      } else {
        await db.propertyComputedCache.add(cache);
      }
    } catch (error) {
      console.error('[PropertyRollupService] Error setting cache:', error);
      // Don't throw - caching is best-effort
    }
  }

  /**
   * Invalidate cache (mark as stale)
   */
  async invalidateCache(propertyId: string): Promise<void> {
    try {
      // Mark as stale in Supabase
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        { eq: { property_id: propertyId } }
      );

      // Mark as stale locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.update(cache.id, { is_stale: true });
      }
    } catch (error) {
      console.error('[PropertyRollupService] Error invalidating cache:', error);
      // Don't throw - cache invalidation is best-effort
    }
  }

  /**
   * Clear cache entries for a property
   */
  private async clearCache(propertyId: string): Promise<void> {
    try {
      // Delete from Supabase
      await restDelete('property_computed_cache', { eq: { property_id: propertyId } });

      // Delete locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.delete(cache.id);
      }
    } catch (error) {
      console.error('[PropertyRollupService] Error clearing cache:', error);
      // Don't throw
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const propertyRollupService = new PropertyRollupService();
